import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Trash2, Layers, Zap } from 'lucide-react';

const EMPTY = { sku: '', size: '', color: '', price: '', compare_at_price: '', cost_price: '', stock: '', weight: '', dimensions: { length: '', width: '', height: '' } };

export const QUICK_SIZES = ['S', 'M', 'L', 'XL', '24', '25', '26', '27'];
export const QUICK_COLORS = ['Negro', 'Blanco', 'Rojo', 'Azul'];

export const emptyVariant = (overrides = {}) => ({ ...EMPTY, dimensions: { ...EMPTY.dimensions }, ...overrides });

export const isVariantValid = (v) => (v.size.trim() || v.color.trim()) && Number(v.price) > 0 && v.stock !== '' && Number(v.stock) >= 0;

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
const str = (v) => (v === null || v === undefined || v === 0 ? '' : String(v));

export const serializeVariants = (variants) => variants.map(v => ({
  sku: v.sku || '',
  size: v.size.trim(),
  color: v.color.trim(),
  price: parseFloat(v.price) || 0,
  compare_at_price: numOrNull(v.compare_at_price),
  cost_price: numOrNull(v.cost_price),
  stock: parseInt(v.stock) || 0,
  weight: parseFloat(v.weight) || 0,
  dimensions: {
    length: parseFloat(v.dimensions?.length) || 0,
    width: parseFloat(v.dimensions?.width) || 0,
    height: parseFloat(v.dimensions?.height) || 0,
  },
}));

export const hydrateVariants = (variants = []) => variants.map(v => ({
  sku: v.sku || '',
  size: v.size || '',
  color: v.color || '',
  price: v.price?.toString() ?? '',
  compare_at_price: str(v.compare_at_price),
  cost_price: v.cost_price === null || v.cost_price === undefined ? '' : String(v.cost_price),
  stock: v.stock?.toString() ?? '',
  weight: str(v.weight),
  dimensions: { length: str(v.dimensions?.length), width: str(v.dimensions?.width), height: str(v.dimensions?.height) },
}));

const VariantRow = ({ v, idx, onChange, onRemove }) => {
  const set = (k, val) => onChange(idx, { ...v, [k]: val });
  const setDim = (k, val) => onChange(idx, { ...v, dimensions: { ...v.dimensions, [k]: val } });
  const p = parseFloat(v.price), c = parseFloat(v.compare_at_price);
  const off = p && c > p ? Math.round(((c - p) / c) * 100) : 0;
  return (
    <div className="border rounded-xl p-3 bg-gray-50 space-y-2" data-testid={`variant-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500">
          Variante {idx + 1}{off > 0 && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-700">-{off}%</span>}
        </span>
        <button type="button" onClick={() => onRemove(idx)} className="text-red-500 hover:text-red-700 p-1" data-testid={`variant-remove-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input value={v.size} onChange={e => set('size', e.target.value)} placeholder="Talla (ej. M, 28, 128GB)" data-testid={`variant-size-${idx}`} />
        <Input value={v.color} onChange={e => set('color', e.target.value)} placeholder="Color (ej. Negro)" data-testid={`variant-color-${idx}`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Input type="number" value={v.cost_price} onChange={e => set('cost_price', e.target.value)} placeholder="Costo" data-testid={`variant-cost-${idx}`} />
        <Input type="number" value={v.price} onChange={e => set('price', e.target.value)} placeholder="Precio *" className="font-semibold" data-testid={`variant-price-${idx}`} />
        <Input type="number" value={v.compare_at_price} onChange={e => set('compare_at_price', e.target.value)} placeholder="Antes" data-testid={`variant-compare-${idx}`} />
        <Input type="number" value={v.stock} onChange={e => set('stock', e.target.value)} placeholder="Stock *" data-testid={`variant-stock-${idx}`} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Input type="number" value={v.weight} onChange={e => set('weight', e.target.value)} placeholder="Peso kg" className="text-sm" data-testid={`variant-weight-${idx}`} />
        <Input type="number" value={v.dimensions.length} onChange={e => setDim('length', e.target.value)} placeholder="Largo cm" className="text-sm" data-testid={`variant-length-${idx}`} />
        <Input type="number" value={v.dimensions.width} onChange={e => setDim('width', e.target.value)} placeholder="Ancho cm" className="text-sm" data-testid={`variant-width-${idx}`} />
        <Input type="number" value={v.dimensions.height} onChange={e => setDim('height', e.target.value)} placeholder="Alto cm" className="text-sm" data-testid={`variant-height-${idx}`} />
      </div>
    </div>
  );
};

const QuickChips = ({ label, values, field, variants, onAdd }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-xs text-gray-500 w-14">{label}</span>
    {values.map(val => {
      const exists = variants.some(v => v[field] === val && !v[field === 'size' ? 'color' : 'size']);
      return (
        <button key={val} type="button" disabled={exists} onClick={() => onAdd(field, val)} data-testid={`quick-${field}-${val}`}
          className={`min-w-[40px] h-8 px-2.5 rounded-full border text-xs font-medium transition-colors ${
            exists ? 'bg-orange-50 border-orange-300 text-orange-600 cursor-default' : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400 hover:text-orange-600'
          }`}>
          {exists ? '✓ ' : '+ '}{val}
        </button>
      );
    })}
  </div>
);

export const VariantsEditor = ({ variants, onChange, defaults = {} }) => {
  const update = (idx, v) => onChange(variants.map((x, i) => (i === idx ? v : x)));
  const remove = (idx) => onChange(variants.filter((_, i) => i !== idx));
  const quickAdd = (field, val) => onChange([...variants, emptyVariant({
    [field]: val,
    price: defaults.price || '',
    compare_at_price: defaults.compare_at_price || '',
    cost_price: defaults.cost_price || '',
  })]);
  return (
    <div className="border-t pt-4 space-y-3" data-testid="variants-editor">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Layers className="w-4 h-4" /> Variantes (opcional)</p>
          <p className="text-xs text-gray-500">Talla y/o color, cada una con su costo, precio, precio "antes", stock y medidas</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...variants, emptyVariant()])} data-testid="variant-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar variante
        </Button>
      </div>
      <div className="rounded-xl border border-dashed p-3 space-y-2 bg-white" data-testid="variant-quick-chips">
        <p className="text-xs font-semibold text-gray-600 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-orange-500" /> Añadir rápido (1 clic = 1 variante)</p>
        <QuickChips label="Tallas" values={QUICK_SIZES} field="size" variants={variants} onAdd={quickAdd} />
        <QuickChips label="Colores" values={QUICK_COLORS} field="color" variants={variants} onAdd={quickAdd} />
      </div>
      {variants.map((v, i) => <VariantRow key={i} v={v} idx={i} onChange={update} onRemove={remove} />)}
      {variants.length > 0 && (
        <p className="text-xs text-gray-500" data-testid="variants-summary">
          El precio mostrado será el de la variante más barata y el stock total la suma de las variantes. Completa el stock de cada una.
        </p>
      )}
    </div>
  );
};
